import { useSelector } from 'react-redux';

import { CAROUSEL_EPISODES_LIMIT } from '@/constants';
import { EVENTS, log, logEvent } from '@/services';
import {
  selectUserCompliance,
  useFetchOrganizationPlaylistEpisodesQuery,
  useUpdateOrgRecommendedEpisodesMutation,
} from '@/store';
import { SnackbarUtils } from '@/utils';

export const useSelectedEpisodesToolbar = () => {
  const compliance = useSelector(selectUserCompliance);

  const { data: episodes = [] } = useFetchOrganizationPlaylistEpisodesQuery({
    compliance,
  });

  const [updateOrgRecommendedEpisodes, { isLoading: isUpdating }] =
    useUpdateOrgRecommendedEpisodesMutation();

  const addEpisode = async (episodeId: string) => {
    try {
      if (isUpdating) return;
      if (episodes.some(e => e.episodeId === episodeId)) {
        SnackbarUtils.warning('This episode is already in the playlist');
        return;
      }
      if (episodes.length >= CAROUSEL_EPISODES_LIMIT) {
        SnackbarUtils.warning(
          'You have reached the maximum number of episodes allowed in the playlist',
        );
        return;
      }

      const newEpisodes = [
        ...episodes.map((e, idx) => ({
          episodeId: e.episodeId,
          position: idx + 1,
        })),
        { episodeId, position: episodes.length + 1 },
      ];

      await updateOrgRecommendedEpisodes({
        episodes: newEpisodes,
      }).unwrap();

      logEvent(EVENTS.CONTENT_CURATION_EVENTS.RECOMMENDED_EPISODE_ADD, {
        episodeId,
      });
      SnackbarUtils.success('Episode was successfully added to the playlist');
    } catch (error) {
      log(error);
      SnackbarUtils.error('We were unable to add this episode to the playlist');
    }
  };

  const removeEpisode = async (episodeId: string) => {
    try {
      if (isUpdating) return;
      const newEpisodes = episodes
        .filter(e => e.episodeId !== episodeId)
        .map((e, idx) => ({ episodeId: e.episodeId, position: idx + 1 }));

      await updateOrgRecommendedEpisodes({
        episodes: newEpisodes,
      }).unwrap();
      logEvent(EVENTS.CONTENT_CURATION_EVENTS.RECOMMENDED_EPISODE_REMOVE, {
        episodeId,
      });
    } catch (error) {
      log(error);
      SnackbarUtils.error(
        'We were unable to remove this episode from the playlist',
      );
    }
  };

  const moveEpisodeToDirection = async ({
    episodeId,
    direction,
  }: {
    episodeId: string;
    direction: 'left' | 'right';
  }) => {
    try {
      if (isUpdating) return;
      const isFirstEpisode = episodes[0].episodeId === episodeId;
      const isLastEpisode =
        episodes[episodes.length - 1].episodeId === episodeId;

      if (direction === 'left' && isFirstEpisode) return;
      if (direction === 'right' && isLastEpisode) return;

      const currentIndex = episodes.findIndex(e => e.episodeId === episodeId);
      const newIndex = currentIndex + (direction === 'left' ? -1 : 1);

      const episodesCopy = [...episodes];
      const placeholder = episodesCopy[newIndex];
      episodesCopy[newIndex] = episodesCopy[currentIndex];
      episodesCopy[currentIndex] = placeholder;

      const newEpisodes = episodesCopy.map((e, idx) => ({
        episodeId: e.episodeId,
        position: idx + 1,
      }));

      await updateOrgRecommendedEpisodes({
        episodes: newEpisodes,
      }).unwrap();
      logEvent(EVENTS.CONTENT_CURATION_EVENTS.RECOMMENDED_EPISODE_REORDER, {
        episodeId,
        direction,
      });
    } catch (error) {
      log(error);
      SnackbarUtils.error(
        'We were unable to move this episode in the playlist',
      );
    }
  };

  const checkIsMoveToLeftDisabled = (episodeId: string) => {
    return episodes[0].episodeId === episodeId;
  };

  const checkIsMoveToRightDisabled = (episodeId: string) => {
    return episodes[episodes.length - 1].episodeId === episodeId;
  };

  return {
    removeEpisode,
    moveEpisodeToDirection,
    addEpisode,
    isUpdating,
    checkIsMoveToLeftDisabled,
    checkIsMoveToRightDisabled,
  };
};
